import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X, Search as SearchIcon } from 'lucide-react';
import { useSearchOptions } from '@/features/user-management/onboarding/form-builder';
import type { FormSearchMeta } from '@/features/user-management/onboarding/form-builder';

interface UserOptionPickerProps {
  /** Currently selected email (or '' for "All"). */
  value: string;
  onChange: (next: string) => void;
  /** Visible label rendered above the picker. */
  label: string;
  /**
   * Backend `specific_type` for the underlying utilities-options call —
   * `pncOfficersOptions` for the PNC dropdown, `programAssociateOptions` for
   * Program Associate / Reporting Manager (these share the same source per
   * the postman collection).
   */
  specificType: 'pncOfficersOptions' | 'programAssociateOptions';
  placeholder?: string;
}

/**
 * Search-backed user picker for list filters. Stores the selected user's
 * email (matching the case shape) and shows the full name in the trigger.
 *
 * Reuses `useSearchOptions` so debounce + react-query caching are inherited
 * from the form-builder Search widget — both filter and form pull from the
 * same canonical user source.
 */
export const UserOptionPicker = ({
  value,
  onChange,
  label,
  specificType,
  placeholder = 'All',
}: UserOptionPickerProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const searchMeta = useMemo<FormSearchMeta>(
    () => ({
      options_id: 'not_rqd',
      key: 'email',
      primary_field: 'fullName',
      secondary_fields: ['email'],
      search_column: 'q',
      specific_type: specificType,
    }),
    [specificType],
  );

  const optionsQuery = useSearchOptions({
    searchMeta,
    searchValue: query,
    enabled: open,
  });

  // When the parent clears the value, drop the cached display label.
  useEffect(() => {
    if (!value) setSelectedLabel(null);
  }, [value]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  const display = selectedLabel || value || '';

  return (
    <div>
      <label className="block text-sm font-medium text-rgf-gray-700 mb-2">{label}</label>
      <div className="relative" ref={containerRef}>
        <button
          type="button"
          className="form-input flex items-center justify-between w-full text-left"
          onClick={() => setOpen((v) => !v)}
        >
          <span className={display ? 'text-rgf-gray-900 truncate' : 'text-rgf-gray-400'}>
            {display || placeholder}
          </span>
          <span className="flex items-center gap-1 flex-shrink-0">
            {value && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Clear filter"
                className="text-rgf-gray-400 hover:text-rgf-gray-700"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange('');
                  setSelectedLabel(null);
                  setQuery('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange('');
                    setSelectedLabel(null);
                    setQuery('');
                  }
                }}
              >
                <X className="h-4 w-4" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-rgf-gray-400" />
          </span>
        </button>

        {open && (
          <div className="absolute z-20 mt-1 w-full rounded-lg border border-rgf-gray-200 bg-white shadow-lg">
            <div className="px-2 pt-2">
              <div className="relative">
                <SearchIcon className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-rgf-gray-400 pointer-events-none" />
                <input
                  autoFocus
                  type="text"
                  className="form-input pl-8"
                  placeholder="Type to search…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto py-2">
              {optionsQuery.isLoading || optionsQuery.isFetching ? (
                <div className="px-3 py-2 text-sm text-rgf-gray-500">Loading…</div>
              ) : optionsQuery.error ? (
                <div className="px-3 py-2 text-sm text-red-600">
                  {(optionsQuery.error as Error).message || 'Failed to load options.'}
                </div>
              ) : !optionsQuery.data || optionsQuery.data.length === 0 ? (
                <div className="px-3 py-2 text-sm text-rgf-gray-500">No matches.</div>
              ) : (
                optionsQuery.data.map((row) => (
                  <button
                    type="button"
                    key={String(row.value)}
                    className="w-full text-left px-3 py-2 hover:bg-rgf-gray-50"
                    onClick={() => {
                      onChange(String(row.value));
                      setSelectedLabel(row.primary || String(row.value));
                      setQuery('');
                      setOpen(false);
                    }}
                  >
                    <div className="text-sm text-rgf-gray-900">{row.primary || '—'}</div>
                    {row.secondary && (
                      <div className="text-xs text-rgf-gray-500">{row.secondary}</div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
